import { getRouteApi, Link } from '@tanstack/react-router';
import {
    AlertTriangle,
    ArrowLeft,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    History,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { type Components } from 'react-markdown';

import { EmptyState } from '@apps/web/components/ui/empty_state';
import { Markdown, MarkdownAnchor } from '@apps/web/components/ui/markdown';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    useAdminAgentScratchpadNote,
    useAdminAgentScratchpadNoteRevisions,
} from '@apps/web/features/admin_agent_scratchpad/api/queries';
import {
    computeLineDiff,
    type LineDiffEntry,
} from '@apps/web/features/admin_agent_scratchpad/model/line_diff';
import {
    normalizeScopeTypes,
    type ScratchpadDetailSearch,
    serializeScopeTypes,
} from '@apps/web/features/admin_agent_scratchpad/model/search_params';
import { ScopeBadge } from '@apps/web/features/admin_agent_scratchpad/ui/scope_badge';
import { RelativeTimestamp } from '@apps/web/features/admin_agent_scratchpad/ui/timestamp';
import { VolatilityGlyph } from '@apps/web/features/admin_agent_scratchpad/ui/volatility_glyph';
import { safeMarkdownUrl } from '@apps/web/lib/safe_markdown';
import { cn } from '@apps/web/lib/utils';
import {
    type AdminScratchpadEvidenceRefRead,
    type AdminScratchpadNoteReadResponse,
    type AdminScratchpadRevisionRead,
} from '@libraries/typescript/api_client';

const REVISIONS_PER_PAGE = 10;
// Canonical Linear workspace slug for phoebe-health tickets. Every other
// admin surface points to `linear.app/phoebework`; the earlier draft used
// `phoebe-health` and would deep-link ticket refs to a 404.
const LINEAR_TICKET_URL = 'https://linear.app/phoebework/issue/';
const GITHUB_MAIN_URL = 'https://github.com/phoebe-health/phoebe/blob/main/';

const detailRoute = getRouteApi('/_app/admin/agent/scratchpad/$note-id');

/**
 * `/admin/agent/scratchpad/$note-id` — full note detail with lazy-loaded
 * revision history. Vault-style column, generous vertical rhythm, headings
 * and code blocks tuned to match the run trace views.
 */
export function ScratchpadNoteDetailPage({
    noteId,
    search,
}: {
    readonly noteId: string;
    readonly search: ScratchpadDetailSearch;
}) {
    const {
        data: note,
        isLoading,
        error,
    } = useAdminAgentScratchpadNote(noteId);
    const [showRevisions, setShowRevisions] = useState(false);

    if (isLoading && note === undefined) {
        return (
            <PageShell search={search}>
                <div className="flex flex-col gap-6">
                    <Skeleton className="h-8 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-72 w-full" />
                </div>
            </PageShell>
        );
    }
    if (error !== undefined || note === undefined) {
        return (
            <PageShell search={search}>
                <EmptyState
                    icon={AlertTriangle}
                    title="Note not found or failed to load."
                    description="It may have been retired or the id may be wrong. Return to the scratchpad and try again."
                />
            </PageShell>
        );
    }

    return (
        <PageShell search={search}>
            <NoteDetailBody
                note={note}
                showRevisions={showRevisions}
                onToggleRevisions={() => {
                    setShowRevisions((previous) => !previous);
                }}
                revisionsPage={search.revisions_page}
            />
        </PageShell>
    );
}

function PageShell({
    children,
    search,
}: {
    readonly children: React.ReactNode;
    readonly search: ScratchpadDetailSearch;
}) {
    // Strip the detail-only param before propagating URL state back to the
    // index — its schema doesn't declare `revisions_page`.
    const { revisions_page: _detailOnly, ...backSearch } = search;
    const canonicalBackSearch = {
        ...backSearch,
        scope_type: serializeScopeTypes(normalizeScopeTypes(backSearch)),
    };
    return (
        <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-5 px-6 pt-6 pb-16 lg:px-10">
            <Link
                to="/admin/agent/scratchpad"
                search={canonicalBackSearch}
                className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-xs transition-colors"
            >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to scratchpad
            </Link>
            {children}
        </div>
    );
}

function NoteDetailBody({
    note,
    showRevisions,
    onToggleRevisions,
    revisionsPage,
}: {
    readonly note: AdminScratchpadNoteReadResponse;
    readonly showRevisions: boolean;
    readonly onToggleRevisions: () => void;
    readonly revisionsPage: number;
}) {
    const isRetired = note.retired_at !== null;
    const orgTimezone = note.scope.organization_timezone;
    return (
        <article className="flex flex-col gap-6">
            {isRetired && note.retired_at !== null ? (
                <RetiredBanner
                    retiredAt={note.retired_at}
                    retiredReason={note.retired_reason}
                    orgTimezone={orgTimezone}
                />
            ) : null}
            <header className="flex flex-col gap-2">
                <span className="flex flex-wrap items-center gap-2">
                    <VolatilityGlyph volatility={note.volatility} />
                    <h1
                        className={cn(
                            'text-2xl font-semibold tracking-tight',
                            isRetired && 'line-through decoration-2',
                        )}
                    >
                        {note.title}
                    </h1>
                    <span className="text-muted-foreground/60 text-[11px] tracking-wider uppercase">
                        v{note.version}
                    </span>
                </span>
                <p className="text-muted-foreground max-w-prose text-base leading-relaxed">
                    {note.one_liner}
                </p>
                <NoteMetadata note={note} orgTimezone={orgTimezone} />
            </header>
            <NoteBody body={note.body} />
            <RunAttribution note={note} />
            <EvidenceRefs refs={note.evidence_refs} />
            <RevisionHistoryPanel
                noteId={note.id}
                revisionCount={note.revision_count}
                showRevisions={showRevisions}
                onToggleRevisions={onToggleRevisions}
                revisionsPage={revisionsPage}
                orgTimezone={orgTimezone}
            />
        </article>
    );
}

function RetiredBanner({
    retiredAt,
    retiredReason,
    orgTimezone,
}: {
    readonly retiredAt: string;
    readonly retiredReason: string | null;
    readonly orgTimezone: string | null | undefined;
}) {
    return (
        <div className="rounded-lg border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-900 dark:border-rose-500/40 dark:bg-rose-900/30 dark:text-rose-100">
            <p className="font-semibold">This note is retired.</p>
            <p className="mt-1 text-xs text-rose-900/85 dark:text-rose-100/85">
                {retiredReason ?? 'Reason not recorded.'}
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-[11px] text-rose-900/75 dark:text-rose-100/70">
                <span>Retired</span>
                <RelativeTimestamp
                    iso={retiredAt}
                    organizationTimezone={orgTimezone}
                    className="text-inherit"
                />
            </p>
        </div>
    );
}

function NoteMetadata({
    note,
    orgTimezone,
}: {
    readonly note: AdminScratchpadNoteReadResponse;
    readonly orgTimezone: string | null | undefined;
}) {
    return (
        <dl className="text-muted-foreground/85 mt-1 grid grid-cols-[max-content_1fr] items-baseline gap-x-4 gap-y-1 text-xs sm:grid-cols-[max-content_1fr_max-content_1fr]">
            <dt className="text-muted-foreground/60 text-[10px] tracking-widest uppercase">
                Scope
            </dt>
            <dd>
                <NoteScopeLink note={note} />
            </dd>
            {note.tags.length > 0 ? (
                <Fragment>
                    <dt className="text-muted-foreground/60 text-[10px] tracking-widest uppercase">
                        Tags
                    </dt>
                    <dd className="flex flex-wrap gap-1">
                        {note.tags.map((tag) => (
                            <span
                                key={tag}
                                className="bg-muted/70 text-foreground/80 rounded-sm px-1.5 py-0.5 text-[10px] font-medium"
                            >
                                #{tag}
                            </span>
                        ))}
                    </dd>
                </Fragment>
            ) : null}
            <dt className="text-muted-foreground/60 text-[10px] tracking-widest uppercase">
                Verified
            </dt>
            <dd>
                <RelativeTimestamp
                    iso={note.last_verified_at}
                    organizationTimezone={orgTimezone}
                />
            </dd>
            <dt className="text-muted-foreground/60 text-[10px] tracking-widest uppercase">
                Updated
            </dt>
            <dd>
                <RelativeTimestamp
                    iso={note.updated_at}
                    organizationTimezone={orgTimezone}
                />
            </dd>
            <dt className="text-muted-foreground/60 text-[10px] tracking-widest uppercase">
                Created
            </dt>
            <dd>
                <RelativeTimestamp
                    iso={note.created_at}
                    organizationTimezone={orgTimezone}
                />
            </dd>
        </dl>
    );
}

// Scratchpad notes are agent-authored, so their body markdown gets the same
// inert-content treatment the safe artifact renderer uses: images never fetch
// remote URLs, links with unusable schemes render as inert text, and http(s)
// anchors go through `MarkdownAnchor` (which forces safe rel/target).
const SAFE_NOTE_MARKDOWN_COMPONENTS: Components = {
    a: ({ node, href, children, ...props }) => {
        const safeHref = href ? safeMarkdownUrl(href) : '';
        if (!safeHref) {
            return (
                <span data-testid="scratchpad-inert-link" {...props}>
                    {children}
                </span>
            );
        }
        return (
            <MarkdownAnchor href={safeHref} {...props}>
                {children}
            </MarkdownAnchor>
        );
    },
    img: ({ node, alt }) => (
        <span
            data-testid="scratchpad-omitted-image"
            className="text-muted-foreground text-xs"
        >
            [{alt || 'image'} omitted]
        </span>
    ),
};

function NoteBody({ body }: { readonly body: string }) {
    return (
        <div className="prose prose-slate prose-sm dark:prose-invert max-w-prose leading-7 [&_code]:font-mono [&_pre]:rounded-md [&_pre]:border [&_pre]:bg-slate-900/95 [&_pre]:text-slate-50">
            <Markdown
                skipHtml={true}
                urlTransform={safeMarkdownUrl}
                components={SAFE_NOTE_MARKDOWN_COMPONENTS}
            >
                {body}
            </Markdown>
        </div>
    );
}

function NoteScopeLink({
    note,
}: {
    readonly note: AdminScratchpadNoteReadResponse;
}) {
    if (note.scope_type === 'organization' && note.scope_id !== null) {
        return (
            <Link
                to="/admin/accounts/$accountId"
                params={{ accountId: note.scope_id }}
                className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
            >
                <ScopeBadge
                    scopeType={note.scope_type}
                    scopeId={note.scope_id}
                    label={note.scope.organization_name}
                />
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </Link>
        );
    }
    return (
        <ScopeBadge
            scopeType={note.scope_type}
            scopeId={note.scope_id}
            label={note.scope.organization_name}
        />
    );
}

function RunAttribution({
    note,
}: {
    readonly note: AdminScratchpadNoteReadResponse;
}) {
    if (note.created_by_run_id === null && note.updated_by_run_id === null) {
        return null;
    }
    return (
        <p className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
            {note.created_by_run_id !== null ? (
                <RunLink runId={note.created_by_run_id} prefix="Created by" />
            ) : null}
            {note.updated_by_run_id !== null &&
            note.updated_by_run_id !== note.created_by_run_id ? (
                <RunLink runId={note.updated_by_run_id} prefix="Updated by" />
            ) : null}
        </p>
    );
}

function RunLink({
    runId,
    prefix,
}: {
    readonly runId: string;
    readonly prefix: string;
}) {
    return (
        <Link
            to="/admin/agent/$runId"
            params={{ runId }}
            className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
            <span>{prefix} run</span>
            <span className="font-mono text-[11px]">{runId.slice(0, 8)}</span>
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </Link>
    );
}

function EvidenceRefs({
    refs,
}: {
    readonly refs: ReadonlyArray<AdminScratchpadEvidenceRefRead>;
}) {
    if (refs.length === 0) {
        return null;
    }
    return (
        <section className="flex flex-col gap-2">
            <h2 className="text-muted-foreground text-[11px] font-semibold tracking-widest uppercase">
                Evidence refs
            </h2>
            <ul className="flex flex-col gap-1.5">
                {refs.map((ref, index) => (
                    <li key={`${ref.kind}-${ref.ref}-${index}`}>
                        <EvidenceRefEntry ref_={ref} />
                    </li>
                ))}
            </ul>
        </section>
    );
}

// Common visual shell for every resolved evidence ref so keyboard focus,
// hover, and layout stay identical whether the row is a link or inert.
const EVIDENCE_ROW_CLASSES =
    'text-foreground/85 hover:text-foreground inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-1 text-xs transition-colors hover:border-border';

/**
 * Route every canonical evidence kind to the surface that can actually
 * render it. Malformed / unknown refs stay non-clickable so a broken URL
 * doesn't silently ship as a real link.
 */
function EvidenceRefEntry({
    ref_,
}: {
    readonly ref_: AdminScratchpadEvidenceRefRead;
}) {
    const displayLabel = ref_.label ?? ref_.ref;
    switch (ref_.kind) {
        case 'url': {
            if (!isHttpUrl(ref_.ref)) {
                return <InertRefBadge ref_={ref_} />;
            }
            return (
                <a
                    href={ref_.ref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={EVIDENCE_ROW_CLASSES}
                >
                    <RefKindTag kind="url" />
                    <span>{displayLabel}</span>
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
            );
        }
        case 'run': {
            if (!looksLikeUuid(ref_.ref)) {
                return <InertRefBadge ref_={ref_} />;
            }
            return (
                <Link
                    to="/admin/agent/$runId"
                    params={{ runId: ref_.ref }}
                    className={EVIDENCE_ROW_CLASSES}
                >
                    <RefKindTag kind="run" />
                    <span className="font-mono text-[11px]">
                        {ref_.ref.slice(0, 8)}
                    </span>
                    {ref_.label ? <span>· {ref_.label}</span> : null}
                </Link>
            );
        }
        case 'ticket': {
            const ticketId = normalizeTicketId(ref_.ref);
            if (ticketId === null) {
                return <InertRefBadge ref_={ref_} />;
            }
            return (
                <a
                    href={`${LINEAR_TICKET_URL}${ticketId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={EVIDENCE_ROW_CLASSES}
                >
                    <RefKindTag kind="ticket" />
                    <span>{ticketId}</span>
                    {ref_.label ? <span>· {ref_.label}</span> : null}
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
            );
        }
        case 'file': {
            const path = normalizeRepoPath(ref_.ref);
            if (path === null) {
                return <InertRefBadge ref_={ref_} />;
            }
            return (
                <a
                    href={`${GITHUB_MAIN_URL}${path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={EVIDENCE_ROW_CLASSES}
                >
                    <RefKindTag kind="file" />
                    <span className="font-mono text-[11px]">{path}</span>
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
            );
        }
        case 'org': {
            if (!looksLikeUuid(ref_.ref)) {
                return <InertRefBadge ref_={ref_} />;
            }
            return (
                <Link
                    to="/admin/accounts/$accountId"
                    params={{ accountId: ref_.ref }}
                    className={EVIDENCE_ROW_CLASSES}
                >
                    <RefKindTag kind="org" />
                    <span>{displayLabel}</span>
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
            );
        }
        case 'artifact': {
            // Artifact ids are unique per run; without the owning run id we
            // cannot build a resolvable link, so a legacy row without one
            // still renders as an inert badge instead of a broken URL. When
            // both ids validate, deep-link into the run page carrying the
            // artifact id in `search` so the run mounts with the Artifacts
            // tab focused on the exact artifact — not the run overview.
            if (ref_.run_id === null || ref_.run_id === undefined) {
                return <InertRefBadge ref_={ref_} />;
            }
            if (!looksLikeUuid(ref_.run_id) || !looksLikeUuid(ref_.ref)) {
                return <InertRefBadge ref_={ref_} />;
            }
            return (
                <a
                    href={`/admin/agent/runs/${ref_.run_id}/artifacts/${ref_.ref}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={EVIDENCE_ROW_CLASSES}
                >
                    <RefKindTag kind="artifact" />
                    <span className="font-mono text-[11px]">
                        {ref_.ref.slice(0, 8)}
                    </span>
                    {ref_.label ? <span>· {ref_.label}</span> : null}
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
            );
        }
        case 'other': {
            return <InertRefBadge ref_={ref_} />;
        }
        default: {
            const exhaustive: never = ref_.kind;
            // Any future new kind lands here without silently going missing.
            void exhaustive;
            return <InertRefBadge ref_={ref_} />;
        }
    }
}

function InertRefBadge({
    ref_,
}: {
    readonly ref_: AdminScratchpadEvidenceRefRead;
}) {
    return (
        <span className={EVIDENCE_ROW_CLASSES}>
            <RefKindTag kind={ref_.kind} />
            <span className="font-mono text-[11px]">{ref_.ref}</span>
            {ref_.label ? <span>· {ref_.label}</span> : null}
        </span>
    );
}

function RefKindTag({
    kind,
}: {
    readonly kind: AdminScratchpadEvidenceRefRead['kind'];
}) {
    return (
        <span className="text-muted-foreground/70 text-[10px] tracking-wider uppercase">
            {kind}
        </span>
    );
}

function isHttpUrl(value: string): boolean {
    return /^https?:\/\//iu.test(value);
}

function looksLikeUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
        value,
    );
}

function normalizeTicketId(value: string): string | null {
    // Accept both bare identifiers (PHO-1234) and full Linear URLs so the
    // agent can write either form without needing string surgery upstream.
    const trimmed = value.trim();
    const ticketPattern = /^[A-Z]{2,10}-\d+$/u;
    if (ticketPattern.test(trimmed)) {
        return trimmed;
    }
    const match = trimmed.match(/([A-Z]{2,10}-\d+)/u);
    return match !== null ? (match[1] ?? null) : null;
}

function normalizeRepoPath(value: string): string | null {
    // File refs must be repo-relative posix paths. Drop leading slashes to
    // keep the concatenation stable, refuse absolute-looking URLs so we
    // don't accidentally route them into GitHub-blob under the same URL.
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.includes('..')) {
        return null;
    }
    if (/^https?:\/\//iu.test(trimmed)) {
        return null;
    }
    return trimmed.replace(/^\/+/, '');
}

function RevisionHistoryPanel({
    noteId,
    revisionCount,
    showRevisions,
    onToggleRevisions,
    revisionsPage,
    orgTimezone,
}: {
    readonly noteId: string;
    readonly revisionCount: number;
    readonly showRevisions: boolean;
    readonly onToggleRevisions: () => void;
    readonly revisionsPage: number;
    readonly orgTimezone: string | null | undefined;
}) {
    if (revisionCount === 0) {
        return null;
    }
    return (
        <section className="border-border/50 flex flex-col gap-3 rounded-lg border p-4">
            <button
                type="button"
                aria-expanded={showRevisions}
                onClick={onToggleRevisions}
                className="text-muted-foreground hover:text-foreground -mx-1 flex items-center justify-between rounded-md px-1 py-1 transition-colors"
            >
                <span className="flex items-center gap-2 text-xs font-semibold tracking-widest uppercase">
                    <History className="h-3.5 w-3.5" aria-hidden="true" />
                    Revisions
                    <span className="text-muted-foreground/70 font-normal tracking-normal normal-case">
                        · {revisionCount}
                    </span>
                </span>
                <ChevronDown
                    className={cn(
                        'h-4 w-4 transition-transform',
                        showRevisions && 'rotate-180',
                    )}
                />
            </button>
            {showRevisions ? (
                <RevisionsList
                    noteId={noteId}
                    revisionsPage={revisionsPage}
                    revisionCount={revisionCount}
                    orgTimezone={orgTimezone}
                />
            ) : (
                <p className="text-muted-foreground/80 text-xs">
                    Expand to load prior revisions. Loaded on demand to keep the
                    base note request cheap.
                </p>
            )}
        </section>
    );
}

function RevisionsList({
    noteId,
    revisionsPage,
    revisionCount,
    orgTimezone,
}: {
    readonly noteId: string;
    readonly revisionsPage: number;
    readonly revisionCount: number;
    readonly orgTimezone: string | null | undefined;
}) {
    const navigate = detailRoute.useNavigate();
    const { data, isLoading, error } = useAdminAgentScratchpadNoteRevisions(
        noteId,
        { page: revisionsPage, perPage: REVISIONS_PER_PAGE },
    );

    if (isLoading && data === undefined) {
        return (
            <div className="flex flex-col gap-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
            </div>
        );
    }
    if (error !== undefined || data === undefined) {
        return (
            <p className="text-destructive text-xs">
                Failed to load revisions. Try again from the run page.
            </p>
        );
    }
    if (data.revisions.length === 0) {
        return (
            <p className="text-muted-foreground/80 text-xs">
                No revisions on file.
            </p>
        );
    }
    const totalPages = Math.max(
        1,
        Math.ceil(revisionCount / REVISIONS_PER_PAGE),
    );

    return (
        <div className="flex flex-col gap-3">
            <ol className="flex flex-col gap-3">
                {data.revisions.map((revision, index) => {
                    // Compare each revision to the successor above it. On
                    // page 1 that's the current note body; on later pages
                    // the server precomputes `comparison_body` as the last
                    // revision on the immediately-newer page, so the diff
                    // stays correct across page boundaries instead of
                    // silently skipping every revision above the current
                    // page (previous behavior).
                    const newerBody =
                        index === 0
                            ? data.comparison_body
                            : (data.revisions[index - 1]?.body ?? '');
                    return (
                        <RevisionEntry
                            key={revision.id}
                            revision={revision}
                            newerBody={newerBody}
                            orgTimezone={orgTimezone}
                        />
                    );
                })}
            </ol>
            {totalPages > 1 ? (
                <RevisionsPagination
                    page={revisionsPage}
                    totalPages={totalPages}
                    onPageChange={(nextPage) => {
                        void navigate({
                            search: (previous) => ({
                                ...previous,
                                revisions_page: nextPage,
                            }),
                            replace: true,
                        });
                    }}
                />
            ) : null}
        </div>
    );
}

function RevisionsPagination({
    page,
    totalPages,
    onPageChange,
}: {
    readonly page: number;
    readonly totalPages: number;
    readonly onPageChange: (page: number) => void;
}) {
    return (
        <nav
            aria-label="Revision pagination"
            className="text-muted-foreground flex items-center justify-between gap-3 text-[11px]"
        >
            <span className="tabular-nums">
                Page {page.toLocaleString()} of {totalPages.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => {
                        onPageChange(page - 1);
                    }}
                    className="hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
                >
                    <ChevronLeft className="h-3 w-3" aria-hidden="true" />
                    Newer
                </button>
                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => {
                        onPageChange(page + 1);
                    }}
                    className="hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
                >
                    Older
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </button>
            </span>
        </nav>
    );
}

function RevisionEntry({
    revision,
    newerBody,
    orgTimezone,
}: {
    readonly revision: AdminScratchpadRevisionRead;
    readonly newerBody: string;
    readonly orgTimezone: string | null | undefined;
}) {
    // React-controlled disclosure so we only pay for `computeLineDiff` when
    // the row is actually expanded. Native `<details>` alone still executed
    // every child on render, which for a 10-row page meant N diffs the user
    // may never look at.
    const [isExpanded, setIsExpanded] = useState(false);
    return (
        <li className="border-border/40 flex flex-col gap-2 rounded-md border bg-white/60 p-3 dark:bg-slate-900/40">
            <span className="text-muted-foreground flex flex-wrap items-center gap-2 text-[11px]">
                <span className="font-mono">v{revision.version}</span>
                <VolatilityGlyph volatility={revision.volatility} />
                <RelativeTimestamp
                    iso={revision.written_at}
                    prefix="written"
                    organizationTimezone={orgTimezone}
                />
                {revision.written_by_run_id !== null ? (
                    <Link
                        to="/admin/agent/$runId"
                        params={{ runId: revision.written_by_run_id }}
                        className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
                    >
                        <span className="font-mono">
                            {revision.written_by_run_id.slice(0, 8)}
                        </span>
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </Link>
                ) : null}
            </span>
            <p className="text-foreground/90 text-sm">
                <span className="font-medium">{revision.title}</span>
                <span className="text-muted-foreground">
                    {' '}
                    — {revision.one_liner}
                </span>
            </p>
            <div className="text-xs">
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => setIsExpanded((value) => !value)}
                    className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                >
                    Diff vs. newer
                </button>
                {isExpanded ? (
                    <RevisionDiff
                        olderBody={revision.body}
                        newerBody={newerBody}
                    />
                ) : null}
            </div>
        </li>
    );
}

function RevisionDiff({
    olderBody,
    newerBody,
}: {
    readonly olderBody: string;
    readonly newerBody: string;
}) {
    // Recompute only when either body reference changes. The bounded LCS
    // (with prefix/suffix fallback above the safety budget) makes this
    // safe even on notes that fill the 20k-character body limit with short
    // lines.
    const diff = useMemo(
        () => computeLineDiff(olderBody, newerBody),
        [olderBody, newerBody],
    );
    return (
        <pre
            data-testid="scratchpad-revision-diff"
            className="border-border/40 mt-2 max-h-96 overflow-auto rounded-md border bg-slate-950/95 p-2 font-mono text-[11px] leading-5 text-slate-100"
        >
            <code>
                {diff.map((entry, index) => (
                    <DiffLine key={index} entry={entry} />
                ))}
            </code>
        </pre>
    );
}

function DiffLine({ entry }: { readonly entry: LineDiffEntry }) {
    const prefix =
        entry.op === 'added' ? '+ ' : entry.op === 'removed' ? '- ' : '  ';
    const className =
        entry.op === 'added'
            ? 'bg-emerald-900/40 text-emerald-100'
            : entry.op === 'removed'
              ? 'bg-rose-900/40 text-rose-100'
              : 'text-slate-300';
    return (
        <span className={cn('block whitespace-pre-wrap', className)}>
            {prefix}
            {entry.text === '' ? ' ' : entry.text}
        </span>
    );
}
